"use client";

import { Reveal } from "@/components/ui/Reveal";
import Image from "next/image";
import { motion } from "framer-motion";

const CLINIC_FEATURES = [
  {
    title: "Advanced Technology",
    desc: "Imported instruments from USA and UK for minimal trauma and precise results.",
    image: "/images/why-choose-us/advanced-technology.webp",
  },
  {
    title: "Highly Skilled Doctors",
    desc: "Surgeons who have set global standards in hair transplants and skin treatments.",
    image: "/images/why-choose-us/highly-skilled-doctors.webp",
  },
  {
    title: "Safety First",
    desc: "Strictly sterile environments to minimize any risk of infection or contamination.",
    image: "/images/why-choose-us/safety-first.webp",
  },
  {
    title: "Affordable Cost",
    desc: "Cost-effective solutions without ever compromising on the quality of care.",
    image: "/images/why-choose-us/affordable-cost.webp",
  },
];

export function WhyDermalife({
  eyebrow,
  title,
  highlightText,
  description,
  items,
}: {
  eyebrow?: string;
  title?: string;
  highlightText?: string;
  description?: string;
  items?: Array<{
    title: string;
    description: string;
    image: string;
  }>;
} = {}) {
  const finalEyebrow = eyebrow || "Clinic Overview";
  const finalTitle = title || "Why Choose";
  const finalHighlightText = highlightText !== undefined ? highlightText : "Dermalife?";
  const finalDescription = description || "Experience the pinnacle of hair restoration technology combined with artistic surgical precision.";
  const finalItems = items && items.length > 0
    ? items
    : CLINIC_FEATURES.map(f => ({
        title: f.title,
        description: f.desc,
        image: f.image
      }));

  return (
    <section id="why-us" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="w-full px-[5%] max-w-7xl mx-auto relative z-10">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-accent text-xs uppercase text-brand-orange-deep">
              {finalEyebrow}
            </span>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold text-brand-black mb-4">
              {finalTitle}{" "}
              {finalHighlightText && (
                <span className="text-brand-orange">{finalHighlightText}</span>
              )}
            </h2>
            <div
              className="text-lg text-brand-gray leading-relaxed [&>p]:mt-2 text-justify"
              dangerouslySetInnerHTML={{ __html: finalDescription }}
            />
          </div>
        </Reveal>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {finalItems.map((feature, i) => {
            const bgColors = [
              'bg-[#d6c2aa]/45', // brand-tan-light/45
              'bg-[#ffb699]/25', // brand-peach/25
              'bg-[#f2f2f2]',    // brand-gray-light
              'bg-[#738749]/10'  // brand-green/10
            ];
            const bgColor = bgColors[i % bgColors.length];

            return (
              <Reveal key={i} delay={0.2 + i * 0.1}>
                <div className="group relative h-full">
                  <motion.div 
                    className={`relative flex h-full min-h-[280px] overflow-hidden rounded-3xl ${bgColor} transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-xl`}
                  >
                    {/* Text Column */}
                    <div className="w-[62%] sm:w-[58%] p-6 sm:p-8 flex flex-col justify-center relative z-10">
                      <div className="w-fit rounded-full bg-white/70 px-3 py-1 font-accent text-[10px] uppercase text-brand-black backdrop-blur-sm mb-4">
                        {String(i + 1).padStart(2, '0')} - {feature.title.split(' ')[0]}
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-brand-black mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-brand-gray leading-snug text-justify">
                        {feature.description}
                      </p>
                    </div>

                    {/* Image Column */}
                    <div className="absolute inset-y-0 right-0 w-[42%] sm:w-[46%] overflow-hidden rounded-tl-[3rem]">
                      {feature.image && (
                        <Image
                          src={feature.image}
                          alt={feature.title}
                          fill
                          sizes="(max-width: 768px) 50vw, 25vw"
                          className="object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                    </div>
                  </motion.div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
